<template>
  <div class="engagement-overtime-wrap">
    <div class="engagement-title">Employee engagement over time
      <div class="info-icon pt-5" name="Engagement2" style="display: initial;">
        <i class="las la-info-circle ls-4"></i>
      </div>
      <tippy to="Engagement2"
        interactive="true"
        theme="light amara"
        class="am-tool"
        :allowHTML="true"
        arrow
        placement="right"
      >
        <h3 class="am-card-title pr-1"><b>Employee engagement over time</b></h3>
        <p>
          This is the average engagement score of the company calculated based on the latest conversations with each employee.
        </p>
      </tippy>
    </div>
    <div v-if="!dashboardData.monthwise_engagement_vs_mood_trend" class="am-no-data">
      {{noDataText}}
    </div>
    <div v-if="isLoading && !showChart && dashboardData.monthwise_engagement_vs_mood_trend" style="position: absolute;left:40%;top:40%">
      <v-btn :loading="true" text x-large fab></v-btn>
      <div class="pl-1">Loading...</div>
    </div>
    <div id="engagementChart" v-show="showChart"></div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs-ext/plugin/customParseFormat';

import OverviewMixin from './overview-mixin';

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
am4core.useTheme(am4themes_animated);

export default {
  name: 'EngagementOvertime',

  mixins: [OverviewMixin],

  data () {
    return {
      isLoading: true,
      showChart: false
    };
  },

  mounted () {
    this.createChartData();
  },

  watch: {
    dashboardData: {
      deep: true,
      handler () {
        this.createChartData();
      }
    }
  },

  beforeDestroy () {
    am4core.disposeAllCharts();
  },

  methods: {
    createChartData () {
      if (this.dashboardData.monthwise_engagement_vs_mood_trend) {
        const formattedData = [];
        this.$lodash.each(this.dashboardData.monthwise_engagement_vs_mood_trend, (v) => {
          formattedData.push({
            // month: v.month,
            month: dayjs(v.month).format('MMM \'YY'),
            bullet: this.getImgUrl(`${Math.round(v.engagement_stats.month_mood_average)}`),
            ...v.engagement_stats
          });
        });
        this.chartData = formattedData;
        this.showChart = true;
        this.createEngagementChart(this.chartData);
      } else {
        this.showChart = false;
      }
    },
    getImgUrl (pet) {
      if (pet) {
        const images = require.context('@/assets/', false, /\.png$/);
        return images(`./${pet}.png`);
      }
    },
    createEngagementChart (val) {
      /* Create chart instance */
      var testChart = am4core.create('engagementChart', am4charts.XYChart);
      /* Add data */
      testChart.data = val;
      /* Create axes */
      var categoryAxis = testChart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'month';
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 10;
      categoryAxis.tooltip.disabled = true;
      // categoryAxis.title.text = 'Months';

      const label = categoryAxis.renderer.labels.template;
      // label.dx = 15;
      label.truncate = true;
      label.fontSize = 12;
      // label.wrap = true;
      label.maxWidth = 70;
      label.hideOversized = false;

      /* Create value axis */
      var valueAxis = testChart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.baseValue = 0;
      valueAxis.title.text = 'Score';
      valueAxis.fontSize = 12;
      valueAxis.min = 0;
      valueAxis.max = 5.5;
      valueAxis.strictMinMax = true;
      valueAxis.tooltip.disabled = true;

      testChart.preloader.disabled = true;
      const that = this;
      testChart.events.on('ready', function (ev) {
        that.isLoading = false;
      });

      /* Create series */
      var series1 = testChart.series.push(new am4charts.LineSeries());
      series1.dataFields.valueY = 'month_driver_average';
      series1.dataFields.categoryX = 'month';
      series1.name = 'Engagement score';
      series1.strokeWidth = 3;
      series1.fill = '#050550';
      series1.stroke = am4core.color('#050550');
      series1.tensionX = 1;
      series1.fillOpacity = 0;
      series1.tooltipHTML = `<center><strong>Month {categoryX}</strong></center>
      <hr />
      <table>
      <tr>
        <th align="left">Engagement score</th>
        <td>{valueY}</td>
      </tr>
      <tr>
        <th align="left">Chats initiated</th>
        <td>{chats_initiated}</td>
      </tr>
      <tr>
        <th align="left">Chats completed</th>
        <td>{chats_completed}</td>
      </tr>
      <tr>
        <th align="left">Participation rate</th>
        <td>{participation_rate}</td>
      </tr>
      </table>`;
      // eslint-disable-next-line no-unused-vars
      var bullet1 = series1.bullets.push(new am4charts.CircleBullet());
      var series2 = testChart.series.push(new am4charts.LineSeries());
      series2.dataFields.valueY = 'month_mood_average';
      series2.dataFields.categoryX = 'month';
      series2.name = 'Vibe average';
      series2.tooltipText = 'Mood average: [bold]{valueY}[/]';
      series2.strokeWidth = 3;
      // series2.fill = '#6b6aa9';
      series2.fill = '#fdd835';
      series2.stroke = am4core.color('#fdd835');
      series2.tensionX = 1;
      series2.fillOpacity = 0;
      var bullet2 = series2.bullets.push(new am4charts.CircleBullet());
      var image = bullet2.createChild(am4core.Image);
      image.propertyFields.href = 'bullet';
      image.width = 30;
      image.height = 30;
      image.horizontalCenter = 'middle';
      image.verticalCenter = 'middle';
      testChart.legend = new am4charts.Legend();
      /* Create a cursor */
      testChart.cursor = new am4charts.XYCursor();
    }
  }
};
</script>

<style lang="scss" scoped>
.engagement-overtime-wrap {
  background-color: var(--bg);
  padding: 16px;
  border-radius: 6px;
  // box-shadow: 0 4px 20px 1px rgba(0,0,0,.06), 0 1px 4px rgba(0,0,0,.08) !important;
  box-shadow: 0 4px 16px 11px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  height: 26rem;
  position: relative;
  .am-no-data {
    height: 20rem;
  }
  .engagement-title {
    font-size: var(--content-size);
    font-size: 20px;
    color: var(--color-text);
    padding: 8px 0;
  }
  #engagementChart {
    height: 22rem;
  }
}
</style>
